import React, { useState } from 'react';
import { Form, Input, Button, Alert, Card, Typography, Space, Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { newCert, getCerts } from '../../features/certs/certSlice';
import {
  SafetyCertificateOutlined,
  LinkOutlined,
  BankOutlined,
  CheckCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const CreateCert = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.certs);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleFormSubmit = async (values) => {
    try {
      const { title, institute, link } = values;
      const formParams = { title, institute, link };
      
      await dispatch(newCert(formParams));
      dispatch(getCerts());

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      form.resetFields();
    } catch (err) {
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8 text-center">
        <Space direction="vertical" size="small">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <SafetyCertificateOutlined className="text-2xl text-accent-blue" />
            <Title level={2} className="m-0 text-text-primary font-semibold">
              Add New Certificate
            </Title>
          </div>
          <Text className="text-text-secondary text-base">
            Add your professional certifications and credentials to showcase your expertise
          </Text>
        </Space>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Certificate added successfully!"
          description="Your certification has been added to your portfolio."
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}
      
      {failed && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Submission Failed"
          description="Please check all required fields and try again."
          type="error"
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Main Form Card */}
      <Card 
        className="shadow-medium border border-border-light rounded-xl bg-white"
        bodyStyle={{ padding: '32px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={onFinishFailed}
          size="large"
          className="space-y-6"
        >
          {/* Certificate Information Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <CheckCircleOutlined className="mr-2 text-accent-blue" />
              Certificate Information
            </Title>
            
            <Form.Item 
              label={<span className="text-text-primary font-medium">Certificate Title</span>}
              name="title" 
              rules={[{ required: true, message: 'Please enter the certificate title' }]}
              className="mb-6"
            >
              <Input 
                placeholder="e.g., AWS Certified Solutions Architect, Google Cloud Professional"
                className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
              />
            </Form.Item>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Issuing Organization</span>}
              name="institute" 
              rules={[{ required: true, message: 'Please enter the issuing organization' }]}
              className="mb-6"
            >
              <Input 
                placeholder="e.g., Amazon Web Services, Google Cloud, Microsoft"
                prefix={<BankOutlined className="text-neutral-400" />}
                className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
              />
            </Form.Item>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Certificate Link (Optional)</span>}
              name="link"
              rules={[
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input 
                placeholder="https://credentials.example.com/certificate-id"
                prefix={<LinkOutlined className="text-neutral-400" />}
                className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
              />
            </Form.Item>
          </div>

          <Divider className="border-border-light" />

          {/* Tips Section */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Text className="text-text-secondary text-sm">
              <strong>💡 Pro Tips:</strong>
              <br />
              • Include the full, official name of your certification
              • Add the complete organization name (e.g., "Amazon Web Services" not just "AWS")
              • Provide the direct link to your certificate for easy verification
            </Text>
          </div>

          {/* Submit Section */}
          <div className="pt-6 border-t border-border-light">
            <div className="flex justify-center">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                icon={<PlusOutlined />}
                className="h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200"
              >
                {loading ? 'Adding Certificate...' : 'Add Certificate'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateCert;